/* Canonical capability string constants for the Organization + Workspace layers.
   Always use these constants instead of raw strings to catch typos at development
   time and provide a single source of truth for the permission catalog.

   Backend source of truth: org-permission-resolver.service.ts + org-roles.service.ts */

/* ── Organization capabilities ──────────────────────────────────── */
export const ORG = {
  /* Members */
  MEMBERS_VIEW:       'org.members.view',
  MEMBERS_INVITE:     'org.members.invite',
  MEMBERS_EDIT:       'org.members.edit',
  MEMBERS_REMOVE:     'org.members.remove',

  /* Teams */
  TEAMS_VIEW:         'org.teams.view',
  TEAMS_CREATE:       'org.teams.create',
  TEAMS_EDIT:         'org.teams.edit',

  /* Billing */
  BILLING_VIEW:       'org.billing.view',

  /* Notifications */
  NOTIFICATIONS_VIEW: 'org.notifications.view',
  NOTIFICATIONS_EDIT: 'org.notifications.edit',

  /* Integrations */
  INTEGRATIONS_VIEW:  'org.integrations.view',

  /* Workspaces */
  WORKSPACES_VIEW:    'org.workspaces.view',
  WORKSPACES_CREATE:  'org.workspaces.create',
  WORKSPACES_EDIT:    'org.workspaces.edit',
  WORKSPACES_DELETE:  'org.workspaces.delete',

  /* Roles */
  ROLES_VIEW:         'org.roles.view',
  ROLES_CREATE:       'org.roles.create',
  ROLES_EDIT:         'org.roles.edit',
  ROLES_DELETE:       'org.roles.delete',

  /* Licenses */
  LICENSES_VIEW:      'org.licenses.view',

  /* Branding */
  BRANDING_VIEW:      'org.branding.view',
  BRANDING_EDIT:      'org.branding.edit',
  WHITELABEL:         'org.whitelabel.configure',

  /* Settings */
  SETTINGS_VIEW:      'org.settings.view',
  SETTINGS_EDIT:      'org.settings.edit',

  /* Audit */
  AUDIT_VIEW:         'org.audit.view',
  AUDIT_EXPORT:       'org.audit.export',

  /* Analytics */
  ANALYTICS_VIEW:     'org.analytics.view',
  ANALYTICS_EXPORT:   'org.analytics.export',

  /* Security */
  SECURITY_VIEW:      'org.security.view',
  SECURITY_CONFIGURE: 'org.security.configure',

  /* Support */
  SUPPORT_VIEW:       'org.support.view',
  SUPPORT_CREATE:     'org.support.create',

  /* Misc */
  USAGE_VIEW:         'org.usage.view',
  BACKUP_VIEW:        'org.backup.view',
  BACKUP_CREATE:      'org.backup.create',
  BACKUP_RESTORE:     'org.backup.restore',
  SEARCH_USE:         'org.search.use',
  HEALTH_VIEW:        'org.health.view',
  TEMPLATES_VIEW:     'org.templates.view',
  TEMPLATES_CREATE:   'org.templates.create',
  TEMPLATES_EDIT:     'org.templates.edit',
  TEMPLATES_DELETE:   'org.templates.delete',
};

/* ── Analytics capabilities ─────────────────────────────────────── */
export const ANALYTICS = {
  ORG_USERS:          'analytics.org.users',
  ORG_EXPORT:         'analytics.org.export',
  WORKSPACE_VIEW:     'analytics.workspace.view',
  CRM_BASIC:          'analytics.crm.basic',
  CRM_ADVANCED:       'analytics.crm.advanced',
};

/* ── Workspace capabilities ─────────────────────────────────────── */
export const WS = {
  VIEW:               'ws.view',
  SETTINGS_EDIT:      'ws.settings.edit',
  ASSIGNMENTS_VIEW:   'ws.assignments.view',
  ASSIGNMENTS_MANAGE: 'ws.assignments.manage',
};

/* ── CRM capabilities ───────────────────────────────────────────── */
export const CRM = {
  PEOPLE_VIEW:        'crm.people.view',
  PEOPLE_CREATE:      'crm.people.create',
  PEOPLE_EDIT:        'crm.people.edit',
  PEOPLE_DELETE:      'crm.people.delete',
  COMPANIES_VIEW:     'crm.companies.view',
  COMPANIES_CREATE:   'crm.companies.create',
  COMPANIES_EDIT:     'crm.companies.edit',
  COMPANIES_DELETE:   'crm.companies.delete',
  DEALS_VIEW:         'crm.deals.view',
  DEALS_CREATE:       'crm.deals.create',
  DEALS_EDIT:         'crm.deals.edit',
  DEALS_DELETE:       'crm.deals.delete',
  DEALS_STAGE:        'crm.deals.stage',
  TASKS_VIEW:         'crm.tasks.view',
  TASKS_CREATE:       'crm.tasks.create',
  TASKS_EDIT:         'crm.tasks.edit',
  TASKS_ASSIGN:       'crm.tasks.assign',
  ACTIVITIES_VIEW:    'crm.activities.view',
  ACTIVITIES_CREATE:  'crm.activities.create',
  DOCUMENTS_VIEW:     'crm.documents.view',
  DOCUMENTS_UPLOAD:   'crm.documents.upload',
};

/* ── Developer capabilities ─────────────────────────────────────── */
export const DEV = {
  API_KEYS_VIEW:      'developer.api-keys.view',
  API_KEYS_CREATE:    'developer.api-keys.create',
  API_KEYS_DELETE:    'developer.api-keys.delete',
  WEBHOOKS_VIEW:      'developer.webhooks.view',
  WEBHOOKS_CREATE:    'developer.webhooks.create',
  WEBHOOKS_DELETE:    'developer.webhooks.delete',
  OAUTH_APPS_VIEW:    'developer.oauth-apps.view',
  OAUTH_APPS_CREATE:  'developer.oauth-apps.create',
  OAUTH_APPS_DELETE:  'developer.oauth-apps.delete',
  SDK_ACCESS:         'developer.sdk.access',
};

/* ── AI capabilities ────────────────────────────────────────────── */
export const AI = {
  COPILOT_USE:        'ai.copilot.use',
  EMAIL_ASSIST:       'ai.email-assist.use',
  AGENTS_CONFIGURE:   'ai.agents.configure',
  KB_CREATE:          'ai.knowledge-base.create',
  MODELS_CONFIGURE:   'ai.models.configure',
};

/* Denial reason codes — shown in PermissionDenied UX */
export const DENIAL_REASONS = {
  INSUFFICIENT_PERMISSION: 'insufficient_permission',
  PLAN_RESTRICTION:        'plan_restriction',
  ORG_POLICY:              'org_policy',
  READ_ONLY_MODE:          'read_only',
  SYSTEM_ROLE_LOCKED:      'system_role_locked',
};
